import { Box, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import i18n from "i18next";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { backendService } from "../../../../../services/backend.service";
import type { SftpCredentials } from "../sftpHooks";

type Props = {
	open: boolean;
	onClose: () => void;
	/** Called with the new folder's name and the credentials the vendor needs, once provisioned. */
	onCreated: (folder: string, credentials: SftpCredentials) => void;
};

/**
 * Creates a top-level SFTP folder for a new vendor feed, together with the SFTP user whose home
 * directory it is.
 *
 * One dialog for both because they are one thing: the folder is only reachable through that user, so
 * offering them separately would let ops create a folder nothing can deliver into. The credentials
 * are handed straight to the caller, which shows them once — they are never stored client-side.
 */
export const CreateSftpFolderDialog: FC<Props> = ({ open, onClose, onCreated }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();
	const [name, setName] = useState("");
	const [conflictMessage, setConflictMessage] = useState<string | null>(null);

	const create = useMutation({
		mutationFn: async (folderName: string) => (await backendService.createSftpFolder(folderName)).data,
		onSuccess: (credentials, folderName) => {
			// The browse query holds each prefix for a minute, so without this the root listing keeps
			// serving a cached response that omits the folder just created — it reads as "did that work?".
			void queryClient.invalidateQueries({ queryKey: ["sftp-browse"] });
			track("dept_config_sftp_folder_created", { folder: folderName });
			setName("");
			setConflictMessage(null);
			onCreated(folderName, credentials);
		},
		onError: (error: AxiosError<{ detail?: { message?: string; error_type?: string } }>) => {
			// The server distinguishes "name already taken" from "name not allowed", and the operator
			// needs to know which — but as a TRANSLATED key, never as echoed English server prose. The
			// route raises UIVisibleError, whose payload nests the i18n key at `detail.message` (see
			// UIVisibleError in backend/models/common.py). Anything else falls back to the generic
			// string, so an unexpected shape can never render a raw key or English at a Hebrew user.
			const detail = error.response?.data?.detail;
			const key = detail?.error_type === "UIVisibleError" ? detail.message : undefined;
			setConflictMessage(key && i18n.exists(key) ? t(key) : t("department.config.integrations.sftp.newFolder.error"));
			trackError("create_sftp_folder", { error_message: error.message });
		},
	});

	const trimmed = name.trim();
	// Rejected client-side too, so the operator sees the rule while typing rather than after a
	// round-trip. The backend enforces the same one-segment allowlist.
	const hasSeparator = /[\\/]/.test(trimmed);

	const handleClose = () => {
		setName("");
		setConflictMessage(null);
		onClose();
	};

	return (
		<SystemDialog
			open={open}
			onClose={handleClose}
			maxWidth="xs"
			fullWidth
			title={t("department.config.integrations.sftp.newFolder.title")}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<SystemBanner
						tone="info"
						title={t("department.config.integrations.sftp.newFolder.hint.title")}
						description={t("department.config.integrations.sftp.newFolder.hint.description")}
						dataTestId="sftp-new-folder-hint"
					/>
					<TextField
						label={t("department.config.integrations.sftp.newFolder.label")}
						value={name}
						onChange={(event) => {
							setName(event.target.value);
							setConflictMessage(null);
						}}
						error={hasSeparator || !!conflictMessage}
						helperText={hasSeparator ? t("department.config.integrations.sftp.newFolder.noSlashes") : conflictMessage}
						size="small"
						fullWidth
						autoFocus
						slotProps={{ htmlInput: { "data-testid": "sftp-new-folder-name" } }}
					/>
				</Box>
			}
			cancel={t("cancel")}
			confirm={t("department.config.integrations.sftp.newFolder.create")}
			confirmButtonVariant="primary"
			confirmDisabled={!trimmed || hasSeparator}
			confirmLoading={create.isPending}
			onConfirm={() => create.mutate(trimmed)}
			dataTestId="sftp-new-folder-dialog"
		/>
	);
};
